// Libraries
import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import { API } from 'aws-amplify';
import { Box, Flex } from '@chakra-ui/react';
import { Icon, Typography } from 'antd';
import { Link } from 'react-router-dom';

// Coconut
import { graphqlOperationEx } from '@sendpayments/graphql/fields';
import * as mutations from '@sendpayments/graphql/mutations';

// Portal
import { useRegistration, useVRNs } from '@send-data-hooks';
import { appConfig } from '@send-base/app.config';
import { CorporateRegistration, RetailRegistration } from '@send-base/types';
import CSSReset from '@send-base/atomic-components/pages/utils/CSSReset';

// Style
// @ts-ignore - We don't have type support for less module
import styles from './VRNAcknowledgementPanel.module.less';

// React Shared
import { useFeature } from '@sendpayments/react-shared/hooks/useFeature';

// Shared Constants
import { CoconutTypes } from '@sendpayments/shared-constants/types';
import { PD_4724_USE_JPM_PARENT_ACCOUNT_DETAILS } from '@sendpayments/shared-constants/features';
import { VRN_GENERAL_ACKNOWLEDGEMENT_LIMIT_DATES } from '@sendpayments/shared-constants/vrn';

const { Text } = Typography;
const VRN_ACKNOWLEDGEMENT_KEY = 'PARENT_ACCOUNT';

const checkDateLimit = () => {
  const limitDate = dayjs(VRN_GENERAL_ACKNOWLEDGEMENT_LIMIT_DATES.PARENT_ACCOUNT.AUD);
  return dayjs().isBefore(limitDate);
};

const VrnText = () => {
  return (
    <Text className={styles.message}>
      <strong>{`We've updated our AUD pay-in account details.`}</strong>
      <br />
      <span>
        We’ve updated our AUD pay-in account details. Please use the correct account when transferring funds to Send. For more
        details, click&nbsp;
      </span>
      <Link to={{ pathname: appConfig.sendPaymentMethods }} target={'_blank'}>
        <span style={{ fontSize: '0.9rem', textDecoration: 'none' }}>[here].</span>
      </Link>
    </Text>
  );
};

const checkAccountVrnUpdated = (account: RetailRegistration | CorporateRegistration) => {
  return account?.type === CoconutTypes.RETAIL && account?.affiliateName?.toLowerCase() !== 'pexa';
};

interface VRNAcknowledgementPanelProps {
  staticEnabled?: boolean;
  bannerEnabled?: boolean;
}

/**
 * Dismissible will take priority over static alert if applicable
 * @param staticEnabled
 * @param bannerEnabled
 * @constructor
 */
const VRNAcknowledgementPanel = ({ staticEnabled = false, bannerEnabled = false }: VRNAcknowledgementPanelProps) => {
  const [isBannerVisible, setBannerVisible] = React.useState<boolean>(false);
  const [isStaticVisible, setStaticVisible] = React.useState<boolean>(false);
  const [hasVRN, setHasVRN] = React.useState<boolean>(false);
  const [{ data: account }] = useRegistration();
  const [{ data: virtualAccounts }] = useVRNs();
  const [
    {
      data: { isEnabled: vrnEnabled },
    },
  ] = useFeature({
    feature: PD_4724_USE_JPM_PARENT_ACCOUNT_DETAILS,
    functionName: 'UseJPMParentAccountDetailsFunction',
  });

  // Only apply logic if feature flag is enabled + account matches the criteria + within date limit
  const shouldDisplay = () => vrnEnabled && checkAccountVrnUpdated(account) && checkDateLimit() && hasVRN;

  useEffect(() => {
    if (Array.isArray(virtualAccounts) && virtualAccounts?.length) {
      const filteredVirtualAccounts = virtualAccounts.filter((vrn) => vrn.parentId === account.id);
      setHasVRN(filteredVirtualAccounts.length > 0);
    }
  }, [virtualAccounts, account]);

  useEffect(() => {
    if (shouldDisplay() && bannerEnabled) {
      const acknowledgementItem = account.vrnLegalAcknowledgement ? JSON.parse(account.vrnLegalAcknowledgement) : {};
      const parentAcknowledgement = acknowledgementItem[VRN_ACKNOWLEDGEMENT_KEY] || {};
      setBannerVisible(!parentAcknowledgement?.AUD?.GENERAL);
    }
  }, [vrnEnabled, account, hasVRN]);

  useEffect(() => {
    setStaticVisible(shouldDisplay() && staticEnabled && !isBannerVisible);
  }, [vrnEnabled, isBannerVisible, account, hasVRN]);

  const handleDismiss = React.useCallback(() => {
    const vrnAcknowledgement = account?.vrnLegalAcknowledgement ? JSON.parse(account.vrnLegalAcknowledgement) : {};
    const parentAcknowledgement = vrnAcknowledgement?.[VRN_ACKNOWLEDGEMENT_KEY] || {};
    const currentAUDAcknowledgement = parentAcknowledgement?.AUD || {};
    currentAUDAcknowledgement.GENERAL = dayjs().toISOString();
    parentAcknowledgement.AUD = currentAUDAcknowledgement;
    vrnAcknowledgement[VRN_ACKNOWLEDGEMENT_KEY] = parentAcknowledgement;

    const accountUpdates = {
      id: account.id,
      type: account.type,
      vrnLegalAcknowledgement: JSON.stringify(vrnAcknowledgement),
      updatedBy: account.email,
    };
    API.graphql(
      graphqlOperationEx(mutations.updateCoconut, {
        input: accountUpdates,
      }),
    );
    setBannerVisible(false);
  }, [account]);

  return <>{isBannerVisible ? <VRNDismissibleBanner handleDismiss={handleDismiss} /> : isStaticVisible && <VRNStaticBanner />}</>;
};

const VRNDismissibleBanner = ({ handleDismiss }) => {
  return (
    <div key={VRN_ACKNOWLEDGEMENT_KEY} className={styles.banner}>
      <div className={styles.messageWrapper}>
        <Icon type="info-circle" className={styles.icon} />
        <VrnText />
      </div>
      <button className={styles.closeBtn} onClick={handleDismiss}>
        <Icon type="close" theme="outlined" />
      </button>
    </div>
  );
};

const VRNStaticBanner = () => {
  return (
    <>
      <CSSReset />
      <chakra-scope>
        <Box mb={4}>
          <Flex bgColor="#FDEEB0" p="16px" alignItems="center" borderRadius="4px">
            <Icon type="info-circle" className={styles.icon} />
            <Box flexDirection="column" ml="16px">
              <VrnText />
            </Box>
          </Flex>
        </Box>
      </chakra-scope>
    </>
  );
};

export default VRNAcknowledgementPanel;
